import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import apiClient from '@/api/client'
import { StaffScanPage } from '@/components/scan/StaffScanPage'
import type { User } from '@/types'

/**
 * Staff-phone loyalty scanning — /scan.
 *
 * A mobile-first surface any staff phone can open (this SPA is already an
 * installable PWA): pick an open order, scan the guest's wallet QR with the
 * phone camera, attach the guest. Attach-only — the punch is awarded by the
 * payment path when the till closes the bill, exactly as if the cashier had
 * scanned at the counter. Gated on the per-venue staff_phone_scan_enabled
 * loyalty setting (checked inside StaffScanPage, so a flipped toggle shows a
 * friendly explanation rather than a dead route).
 */
export const Route = createFileRoute('/scan')({
  component: ScanRoute,
})

// Standalone route like /kitchen: not nested under the admin shell, auth read
// from localStorage the same way.
function ScanRoute() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoadingAuth, setIsLoadingAuth] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('pos_token')
    const storedUser = localStorage.getItem('pos_user')
    if (storedUser && token) {
      try {
        setUser(JSON.parse(storedUser))
      } catch {
        apiClient.clearAuth()
      }
    }
    setIsLoadingAuth(false)
  }, [])

  if (isLoadingAuth) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!apiClient.isAuthenticated() || !user) {
    return <Navigate to="/login" replace />
  }

  // Mirrors the server-side counter group (RequireRoles counter/manager/admin)
  // that guards the resolve-card + guest-attach endpoints this page calls.
  const allowed = user.role === 'counter' || user.role === 'manager' || user.role === 'admin'
  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center overflow-y-auto bg-background">
        <div className="max-w-md px-6 text-center">
          <h1 className="mb-2 text-2xl font-bold">Access denied</h1>
          <p className="mb-4 text-muted-foreground">
            Loyalty scanning is available to counter staff and managers.
          </p>
          <button
            onClick={() => (window.location.href = '/')}
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
          >
            Back to POS
          </button>
        </div>
      </div>
    )
  }

  return <StaffScanPage />
}
